import styles from './FormSelect.module.scss';
import { defineComponent, h, PropType, VNode } from 'vue';
import { Option } from '@vaaas/fpts/option';
import { findIndex } from '@vaaas/fpts/iter';
import { Unary } from '@vaaas/fpts/data';
import { do_nothing } from '@phi/util';
import { utility } from '@phi/components';

export type Entry<T> = EntryWithText<T> | EntryWithSlot<T>;

export type EntryWithSlot<T> = {
    text: Unary<T, string | VNode | Array<string | VNode>>;
    value: T;
    disabled?: boolean;
}

export type EntryWithText<T> = {
    text: string;
    value: T;
    disabled?: boolean;
}

const hasSlot = <T>(x: Entry<T>): x is EntryWithSlot<T> => typeof x.text !== 'string';

export default <T>() => defineComponent({
    props: {
        options: {
            required: true,
            type: Array as PropType<Entry<T>[]>,
        },

        value: {
            required: true,
            type: null as any as PropType<Option<T>>,
        },
    },

    emits: {
        'update:value': (x: T) => true,
    },

    data() {
        return {
            selected: this.find_selected!(),
        };
    },

    methods: {
        onChange(e: Event) {
            const elem = e.target as HTMLInputElement;
            const index = parseFloat(elem.value);
            const value = this.options[index]!.value;
            if (value !== undefined)
                this.$emit('update:value', value);
        },

        find_selected() {
            return findIndex<Entry<T>>(x => x.value === this.value)(this.options);
        },
    },

    watch: {
        options() {
            this.selected = this.find_selected();
        },

        value() {
            this.selected = this.find_selected();
        },
    },

    render() {
        if (this.options.every(hasSlot))
            return h('div', {
                class: [
                    'form-select',
                    styles.has_slot,
                    utility.margin_gap_3,
                ],
            }, this.options.map((entry, i) => h('div', {
                key: i,
                tabindex: '0',
                class: {
                    'form-select-item': true,
                    [styles.item]: true,
                    [styles.disabled]: entry.disabled === true,
                },
                onClick: entry.disabled
                    ? do_nothing
                    : () => this.$emit('update:value', entry.value),
            }, [
                h('div', { class: utility.flexible }, entry.text(entry.value)),
                h('div', {
                    class: {
                        [styles.radio]: true,
                        [styles.selected]: i === this.selected,
                    },
                }),
            ])));
        else
            return h('select', {
                class: ['form-select', styles.no_slot],
                onChange: this.onChange,
                value: this.selected,
            }, this.options.map((x, i) => h('option', {
                value: i,
                key: i,
                disabled: x.disabled === true,
                class: {
                    [styles.disabled]: x.disabled === true,
                    [styles.option]: true,
                },
            }, x.text as string)));
    }
});
